import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { Cell } from "react-table";
import { CUSTOM_FIELD_TYPE } from "src/api/contractGroup/contractgroup-type";
import { PartnerMemberCustomFieldModel, PartnerMemberListModel } from "src/api/userAdditionalInfo/userAdditionalInfo-type";
import { BaseTooltip } from "src/components";
import Link from "src/components/PartnerLink";
import { PagePath } from "src/pages/product/details";
import { ViewYmdFormat, formatLargeNumberString, formatPhoneNumber } from "src/utils";
import { useUserAdditionalInfo } from "../../hooks/useUserAdditionalInfo";

// contractSummaryList 의 특정 속성을 (,) 로 join 하여 출력 (말줄임 + 리치툴팁)
const joinContractSummary = (
  row: PartnerMemberListModel,
  key: "contractManageId" | "contractApplyNumber" | "contractName" | "contractNickname",
) => {
  const joined = (row.contractSummaryList || [])
    .map((summary) => summary[key])
    .filter((value) => value !== undefined && value !== null && value !== "")
    .join(", ");
  return joined || "-";
};

// 고정 컬럼
const fixedColumns = [
  {
    Header: "회원번호",
    accessor: "memberNo",
    width: 130,
    Cell: ({ value }: Cell<PartnerMemberListModel>) => {
      return (
        <Link className="text-underline" to={PagePath.userAdditionalInfo.detail.replace(":memberNo", value)}>
          {value || "-"}
        </Link>
      );
    },
  },
  {
    Header: "휴대폰번호",
    accessor: "phoneNumber",
    width: 130,
    Cell: ({ value }: Cell<PartnerMemberListModel>) => <BaseTooltip contents={(value && formatPhoneNumber(value)) || "-"} isSingleLine={true} />,
  },
  {
    Header: "이메일",
    accessor: "email",
    width: 180,
    Cell: ({ value }: Cell<PartnerMemberListModel>) => <BaseTooltip contents={value || "-"} isSingleLine={true} />,
  },
  {
    Header: "닉네임",
    accessor: "nickname",
    width: 120,
    Cell: ({ value }: Cell<PartnerMemberListModel>) => <BaseTooltip contents={value || "-"} isSingleLine={true} />,
  },
  {
    Header: "닉네임(추가)",
    accessor: "secondaryNickname",
    width: 120,
    Cell: ({ value }: Cell<PartnerMemberListModel>) => <BaseTooltip contents={value || "-"} isSingleLine={true} />,
  },
  {
    // 신청계약번호(난수) 대체 컬럼.
    // 한 이용자가 여러 계약관리에 속할 수 있어 값이 쉼표로 이어지므로, 단일 링크를 걸 수 없어 텍스트로만 노출한다.
    Header: "계약관리 ID",
    accessor: "contractManageId",
    width: 160,
    Cell: ({ row }: Cell<PartnerMemberListModel>) => {
      const value = joinContractSummary(row.original, "contractManageId");
      return <BaseTooltip type="large" tooltip={value} contents={value} />;
    },
  },
  {
    Header: "계약명(필수)",
    accessor: "contractName",
    width: 160,
    Cell: ({ row }: Cell<PartnerMemberListModel>) => {
      const value = joinContractSummary(row.original, "contractName");
      return <BaseTooltip type="large" tooltip={value} contents={value} />;
    },
  },
  {
    Header: "계약명(선택)",
    accessor: "contractNickname",
    width: 160,
    Cell: ({ row }: Cell<PartnerMemberListModel>) => {
      const value = joinContractSummary(row.original, "contractNickname");
      return <BaseTooltip type="large" tooltip={value} contents={value} />;
    },
  },
];

// 커스텀 필드를 react-table 컬럼으로 변환 (노출 필드만, orderNum 순)
const createCustomFieldColumns = (customFields: PartnerMemberCustomFieldModel[]) => {
  return customFields
    .filter((field) => field.isVisible && !field.isDeleted)
    .sort((a, b) => (a.orderNum || 0) - (b.orderNum || 0))
    .map((field) => ({
      Header: () => <span className="custom-field-header">{field.fieldName}</span>,
      accessor: `fieldId_${field.fieldId}` as any, // 유니크 키 용도 (실제 값은 inputList 에서 매칭)
      width: field.fieldType === CUSTOM_FIELD_TYPE.CALENDAR ? 110 : 160,
      Cell: ({ row }: Cell<PartnerMemberListModel>) => {
        const inputData = row.original.inputList?.find((input) => input.fieldId === field.fieldId);

        if (!inputData) {
          return field.fieldType === CUSTOM_FIELD_TYPE.CALENDAR ? (
            <p className="custom-field w-100 text-center">-</p>
          ) : (
            <span className="custom-field">-</span>
          );
        }

        let displayValue = inputData.inputValue || "";

        // 단일/다중선택: 선택 옵션명을 (,) 로 구분하여 출력
        if (inputData.optionList && inputData.optionList.length > 0 && field.optionList) {
          displayValue = inputData.optionList
            .map((selected) => field.optionList?.find((opt) => opt.optionId === selected.optionId)?.optionName)
            .filter(Boolean)
            .join(", ");
        }

        if (displayValue) {
          switch (field.fieldType) {
            case CUSTOM_FIELD_TYPE.NUMBER: {
              const formattedValue = formatLargeNumberString(displayValue);
              if (formattedValue) {
                return <BaseTooltip type="large" className="custom-field text-right w-100" tooltip={formattedValue} contents={formattedValue} />;
              }
              break;
            }
            case CUSTOM_FIELD_TYPE.CALENDAR:
              if (moment(displayValue).isValid()) {
                return <p className="custom-field w-100 text-center">{moment(displayValue).format(ViewYmdFormat.YYYY_MM_DD)}</p>;
              }
              break;
            default:
              break;
          }
        }

        if (field.fieldType === CUSTOM_FIELD_TYPE.CALENDAR) {
          return <p className="custom-field w-100 text-center">{displayValue || "-"}</p>;
        }

        return <BaseTooltip className="custom-field" contents={displayValue || "-"} />;
      },
    }));
};

/**
 * 이용자 부가정보 목록 컬럼 hook
 * - 고정 컬럼 + 커스텀 필드(RCR11, 노출 필드) 동적 컬럼
 * - customFields 는 커스텀필드 검색 옵션 구성에도 재사용
 */
export const useUserAdditionalInfoColumns = () => {
  const { getCustomFieldList } = useUserAdditionalInfo();
  const [customFields, setCustomFields] = useState<PartnerMemberCustomFieldModel[]>([]);

  useEffect(() => {
    const fetchCustomFields = async () => {
      const fields = await getCustomFieldList();
      setCustomFields(fields || []);
    };
    fetchCustomFields();
  }, []);

  const columns = useMemo(() => {
    return [...fixedColumns, ...createCustomFieldColumns(customFields)];
  }, [customFields]);

  return { columns, customFields };
};
